<script setup lang="ts">
import type { MatchmakingItem, MatchmakingPost } from '~/types/types'
import AppLink from '@/components/app/AppLink.vue'
import { useGeoDataStore } from '~/stores/geoData'
import { useMatchmakingDataStore } from '~/stores/matchmaking'
import AppMap from '@/components/map/AppMap.vue'
import { LayerPresets } from '~/types/map'
const { find } = useStrapi()
const { slug } = useRoute().params
const { keyTranslator: operatorTranslator } = await useKeyTranslator('operator')
const { keyTranslator: fuelTypeTranslator } = await useKeyTranslator('fuelType')
const { keyTranslator: typeTranslator } = await useKeyTranslator('type')
const { keyTranslator: offerTranslator } = await useKeyTranslator('offer')
const { keyTranslator: offerPublicActorTranslator } = await useKeyTranslator('offerPublicActor')
const { keyTranslator: requirementTranslator } = await useKeyTranslator('requirementLabels')
const { keyTranslator: accessibilityTranslator } = await useKeyTranslator('accessibility')
const { keyTranslator: vehicleTypeTranslator } = await useKeyTranslator('vehicleType')
const { keyTranslator: refillTranslator } = await useKeyTranslator('refill')
const { keyTranslator: capacityTranslator } = await useKeyTranslator('capacity')

const matchmakingStore = useMatchmakingDataStore()
matchmakingStore.updateFilter('slug', [slug as string])
onUnmounted(() => {
  matchmakingStore.updateFilter('slug', [])
})
const { data: matchmakingItem } = await useAsyncData(
  'matchmakings',
  async () => await matchmakingStore.fetchSinglePost(slug as string)
)
const geoDataStore = useGeoDataStore()
const county = computed(() => geoDataStore.getCountyByCountyId(matchmakingItem.value?.county))
await useAsyncData('counties', () => geoDataStore.initCounties())

useSeoMeta({
  title: () => `Drivmedla - ${matchmakingItem.value?.operator_name}`,
  description: () =>
    `Vill du samarbeta med ${matchmakingItem.value?.operator_name}? Läs mer om ${matchmakingItem.value?.operator_name} och se om ni kan hjälpas åt i omställningen till fossilfria drivmedel `,
  ogTitle: () => `Drivmedla - ${matchmakingItem.value?.operator_name}`,
  ogDescription: () =>
    `Vill du samarbeta med ${matchmakingItem.value?.operator_name}? Läs mer om ${matchmakingItem.value?.operator_name} och se om ni kan hjälpas åt i omställningen till fossilfria drivmedel `,
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})
const { matchmakingTypeLabelTranslator } = useMathmakingUtils()
const matchmakingTypeLabel = computed(() => matchmakingTypeLabelTranslator(matchmakingItem.value))
const loadMap = ref(false)
const layerPresets: LayerPresets = {
  'infrastructure-opportunities': { visible: true },
}
onMounted(() => {
  setTimeout(() => {
    loadMap.value = true
  }, 500)
})
</script>
<template>
  <main class="matchmaking-single">
    <AppLink class="link-back" title="Alla inlägg" path="/samarbeta" icon="ArrowLeftIcon" />
    <div class="col-12 md:col-7 matchmaking-single__content">
      <AppHeading :title="operatorTranslator(matchmakingItem?.operator)" type="h2" />
      <AppHeading
        v-if="matchmakingItem?.type"
        :title="typeTranslator(matchmakingItem.type)"
        type="h3"
      />
      <AppHeading
        v-if="matchmakingItem?.offer"
        :title="offerTranslator(matchmakingItem.offer)"
        type="h3"
      />
      <AppHeading
        v-if="matchmakingItem?.requirement"
        :title="requirementTranslator(matchmakingItem.requirement)"
        type="h3"
      />
      <AppHeading
        v-if="matchmakingItem?.offer_public_actor"
        :title="offerPublicActorTranslator(matchmakingItem.offer_public_actor)"
        type="h3"
      />
      <div class="flex" style="margin-left: -0.5rem">
        <div class="col">
          <AppSubItem v-if="matchmakingItem?.fuel_type" :title="matchmakingTypeLabel">
            <AppChips>
              <AppChipsItem
                v-for="(fuelTypeItem, i) in matchmakingItem.fuel_type"
                :key="i"
                :item="fuelTypeTranslator(fuelTypeItem)"
                type="dark"
              />
            </AppChips>
          </AppSubItem>
        </div>
        <div class="col">
          <AppSubItem
            v-if="matchmakingItem?.start_date || matchmakingItem?.end_date"
            title="Giltighetstid"
          >
            <AppChips>
              <AppChipsItem
                v-if="matchmakingItem.start_date"
                :item="matchmakingItem.start_date"
                type="outlined"
              />
              <AppChipsItem
                v-if="matchmakingItem.end_date"
                :item="matchmakingItem.end_date"
                type="outlined"
              />
            </AppChips>
          </AppSubItem>
        </div>
      </div>
      <div class="flex" style="margin-left: -0.5rem">
        <div class="col">
          <AppSubItem v-if="county" title="Plats" :description="county && county.name" />
        </div>
        <div class="col">
          <AppSubItem v-if="matchmakingItem?.address_description" title="Kommentar">
            <p>{{ matchmakingItem.address_description }}</p>
          </AppSubItem>
        </div>
      </div>
      <div class="flex flex-wrap" style="margin-left: -0.5rem">
        <AppSubItem
          v-if="matchmakingItem?.accessibility"
          class="col md:col-6 lg:col-3"
          title="Tillgänglighet"
          :description="accessibilityTranslator(matchmakingItem.accessibility)"
        />
        <AppSubItem
          v-if="matchmakingItem?.fuel_type_volumes"
          class="col md:col-6 lg:col-3"
          title="Drivmedel"
          :description="matchmakingItem.fuel_type_volumes"
        />
        <AppSubItem
          v-if="matchmakingItem?.refill"
          class="col md:col-6 lg:col-3"
          title="Påfyllning"
          :description="refillTranslator(matchmakingItem.refill)"
        />
        <AppSubItem
          v-if="matchmakingItem?.vehicle_type && matchmakingItem?.vehicle_type.length > 0"
          class="col md:col-6 lg:col-3"
          title="Fordonstyp"
          :description="
            matchmakingItem?.vehicle_type.map((type) => vehicleTypeTranslator(type)).join(', ')
          "
        />

        <AppSubItem
          v-if="matchmakingItem?.vehicle_type_amount"
          class="col md:col-6 lg:col-3"
          title="Antal fordon"
          :description="matchmakingItem.vehicle_type_amount"
        />
        <AppSubItem
          v-if="matchmakingItem?.capacity && matchmakingItem?.capacity.length > 0"
          class="col md:col-6 lg:col-3"
          title="Kapacitet"
          :description="matchmakingItem.capacity.map((c) => capacityTranslator(c)).join(', ')"
        />
        <AppSubItem
          v-if="matchmakingItem?.capacity_amount"
          class="col md:col-6 lg:col-3"
          title="Antal laddpunkter"
          :description="matchmakingItem.capacity_amount"
        />
      </div>
      <div v-if="matchmakingItem?.locations" style="height: 50vh; width: 100%" class="mt-2">
        <AppMap v-if="loadMap" :layer-presets="layerPresets" :focus-to-station="matchmakingItem" />
        <NuxtLink to="/kartor-och-statistik/samlat-behov-och-utbud-fran-samarbeta">
          <Button label="Visa alla inlägg på karta" />
        </NuxtLink>
      </div>
    </div>
    <div class="col-12 md:col-5 matchmaking-single__content bg-white">
      <div class="sticky">
        <AppHeading title="Kontaktinformation" type="h2" />
        <AppHeading :title="matchmakingItem?.operator_name" type="h1" />
        <p v-if="matchmakingItem?.operator_description">
          {{ matchmakingItem.operator_description }}
        </p>
        <div class="flex flex-column" style="margin-left: -0.5rem">
          <NuxtLink class="col" :href="`mailto:${matchmakingItem?.mail}`">
            <Button label="Kontakta" />
          </NuxtLink>
          <NuxtLink
            v-if="matchmakingItem?.webpage"
            class="col"
            :href="`https://${matchmakingItem?.webpage}`"
            target="_blank"
          >
            <Button
              label="Gå till extern länk"
              icon="pi pi-external-link"
              icon-pos="right"
              severity="secondary"
              outlined
            />
          </NuxtLink>
        </div>
        <div class="flex flex-column" style="margin-left: -0.5rem">
          <AppSubItem
            v-if="matchmakingItem?.phone"
            class="col"
            title="Telefon"
            :description="matchmakingItem.phone"
          />
          <AppSubItem
            v-if="matchmakingItem?.organisationnumber"
            class="col"
            title="Org.nummer  "
            :description="matchmakingItem.organisationnumber"
          />
        </div>
      </div>
    </div>
  </main>
</template>
<style lang="scss" scoped>
@import 'App.scss';
.sticky {
  position: sticky;
  top: calc($topbarHeight + 1rem);
}
.matchmaking-single {
  display: flex;
  flex-flow: row wrap;

  &__content {
    padding: 5rem 2rem;

    @include tablet-landscape-up {
      min-height: calc(100vh - 8rem);
      padding: 6rem 4rem;
    }
  }
}

.link-back {
  position: absolute;
  left: 2rem;
  top: 6rem;

  @include tablet-landscape-up {
    left: 4rem;
    top: 7rem;
  }
}
</style>
